// openrouterModels.js — Catálogo AO VIVO de modelos do adapter OpenRouter.
//
// O preset estático em adapterPresets.js cobria só ~25 modelos "recomendados".
// A OpenRouter, porém, roteia 350+ modelos e expõe a lista COMPLETA num
// endpoint PÚBLICO (sem API key): GET https://openrouter.ai/api/v1/models.
// Este módulo busca essa lista pro ModelMultiSelect mostrar tudo.
//
// Pré-condições já satisfeitas no app:
//   • CSP: 'https://openrouter.ai' já está em connect-src (index.html).
//   • CORS: o endpoint responde Access-Control-Allow-Origin: *.
// Logo o fetch roda direto do renderer — sem precisar passar pelo Worker.

const OPENROUTER_MODELS_URL = 'https://openrouter.ai/api/v1/models';

// Cache em memória por sessão. A lista muda raramente; refetch a cada abertura
// do dropdown seria desperdício. Guardamos { at, models } e também a promise
// in-flight pra deduplicar chamadas concorrentes (abrir/fechar rápido).
let cache = null;
let inflight = null;
const TTL_MS = 10 * 60 * 1000; // 10 min

/**
 * Extrai os ids de modelo do payload da OpenRouter, deduplicados e ordenados
 * alfabeticamente (354 itens precisam de ordem pra serem navegáveis).
 * Resiliente a payload faltando/malformado → retorna [].
 *
 * @param {unknown} json
 * @returns {string[]}
 */
export function parseOpenRouterModels(json) {
  const data = json && Array.isArray(json.data) ? json.data : [];
  const ids = [];
  for (const m of data) {
    const id = typeof m?.id === 'string' ? m.id.trim() : '';
    if (id) ids.push(id);
  }
  return Array.from(new Set(ids)).sort((a, b) => a.localeCompare(b));
}

/**
 * Busca a lista completa de modelos da OpenRouter (com cache + dedup de
 * chamadas concorrentes). Lança em erro de rede / HTTP não-ok — o caller
 * decide o fallback (no ModelMultiSelect: cai pros presets).
 *
 * @param {object} [opts]
 * @param {boolean} [opts.force] - ignora o cache e refetcha
 * @param {typeof fetch} [opts.fetchImpl] - injeção pra testes
 * @returns {Promise<string[]>}
 */
export async function fetchOpenRouterModels({ force = false, fetchImpl } = {}) {
  if (!force && cache && Date.now() - cache.at < TTL_MS) return cache.models;
  if (!force && inflight) return inflight;

  const doFetch = fetchImpl || (typeof fetch !== 'undefined' ? fetch : null);
  if (!doFetch) throw new Error('fetch indisponível');

  inflight = (async () => {
    const res = await doFetch(OPENROUTER_MODELS_URL, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    const models = parseOpenRouterModels(json);
    cache = { at: Date.now(), models };
    return models;
  })();

  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}

// Registry de loaders de catálogo "ao vivo" por adapter. Hoje só a OpenRouter
// tem catálogo público sem key; os demais providers exigem API key e já têm
// preset curado. Mapa torna trivial adicionar mais loaders no futuro.
const LIVE_LOADERS = {
  openrouter: fetchOpenRouterModels,
};

/**
 * Retorna o loader de catálogo ao vivo do adapter, ou null se não houver.
 * @param {string} adapterName
 * @returns {((opts?: object) => Promise<string[]>) | null}
 */
export function getLiveModelLoader(adapterName) {
  return LIVE_LOADERS[adapterName] || null;
}

// Helper só pra testes: zera o cache entre casos.
export function __resetOpenRouterModelsCache() {
  cache = null;
  inflight = null;
}
